// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { Button, Typography, Chip, Tooltip } from '@mui/material';
import { AppIcon } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { useMemo, type FC } from 'react';
import { Link } from 'react-router';
import ProfilingModal from '../profiling/profiling-modal';
import { useClientData } from '@shared/advisory/client-profile/providers';

export const ProfileHeader: FC = () => {
  const { client, onNotification, notificationSent, statusMap } = useClientData();

  const status = useMemo(
    () => statusMap.get(client.status ?? ''),
    [client.status, statusMap],
  );

  return (
    <header className="mb-4 flex flex-1 items-center justify-between gap-x-4">
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<AppIcon icon="ArrowLeft" />}
        to="/"
        component={Link}
        data-testid="back-button"
      >
        <Translation i18nKey="common.back" ns="common" />
      </Button>

      <div className="mr-auto">
        <Typography variant="h2" data-testid="client-name">
          {client.name}
        </Typography>
        <br />
        <Typography variant="body1" color="GrayText" data-testid="client-profile">
          <Translation i18nKey="client-profile.title" ns="components" />
        </Typography>
      </div>

      <ProfilingModal hidden={status?.id !== 'inactive'} />

      {status?.id === 'pending' && !notificationSent && (
        <Tooltip title="Notify the client">
          <Button
            variant="filled"
            color="secondary"
            startIcon={<AppIcon icon="Notification" size="base" />}
            data-testid="notify-button"
            onClick={onNotification}
          >
            <Translation
              i18nKey="client-profile.notification-button"
              ns="components"
            />
          </Button>
        </Tooltip>
      )}

      {status && (
        <Chip
          label={status.label}
          color={status.color}
          variant="filled"
          size="small"
          data-testid="status-chip"
        />
      )}
    </header>
  );
};
